import * as THREE from "three";

type SetPartVisibilityParams = {
  modelName: string;
  visible: boolean;
  findNodesByName: (name: string) => THREE.Object3D[];
  normalize: (name: string) => string;
};

/**
 * Toggles the visibility of a single named part on or off, independently of any
 * category. Unlike `showPart` (which hides a whole category and reveals one
 * node), this leaves sibling parts untouched, so it suits multi-select
 * `gridOptions` groups where several accessories can be shown at the same time.
 *
 * Hiding only clears object-level visibility (never `material.visible`), so a
 * material shared with other visible parts is not accidentally hidden.
 */
export function setPartVisibility(params: SetPartVisibilityParams): boolean {
  const { modelName, visible, findNodesByName, normalize } = params;

  if (!normalize(modelName)) return false;

  const nodes = findNodesByName(modelName);
  if (nodes.length === 0) return false;

  for (const node of nodes) {
    node.visible = visible;
    node.traverse((descendant) => {
      descendant.visible = visible;
      // When showing, re-enable materials in case they were toggled off
      // elsewhere. When hiding, leave materials alone (see doc comment).
      if (visible && descendant instanceof THREE.Mesh) {
        if (descendant.material instanceof THREE.Material) {
          descendant.material.visible = true;
        } else if (Array.isArray(descendant.material)) {
          descendant.material.forEach((mat) => {
            if (mat instanceof THREE.Material) mat.visible = true;
          });
        }
      }
    });

    // Showing a part is pointless if an ancestor is still hidden.
    if (visible) {
      let parent = node.parent;
      while (parent) {
        parent.visible = true;
        parent = parent.parent;
      }
    }
  }

  return true;
}
